import React from 'react';
import { ArrowLeftIcon, XIcon } from './icons';
/**
 * SlideOver — a reusable full-screen slide-in panel.
 *
 * The default navigation pattern for multi-step flows: each layer slides in
 * from the right over the previous one. The header offers two affordances:
 *
 *   • Back  (←) — pops just this layer (optional; omit on a root layer)
 *   • Close (×) — tears the whole stack down and returns to the origin
 *
 * Stack several SlideOvers and drive their `open` state from a single `view`
 * value to build a layered flow (e.g. list → detail → player). Wire every
 * layer's `onClose` to the same "close everything" handler so the × always
 * exits the entire flow.
 */
export function SlideOver({
  open,
  onBack,
  onClose,
  eyebrow,
  eyebrowClass = 'text-coral-deep',
  title,
  z = 'z-20',
  padded = true,
  direction = 'right',
  scroll = true,
  children
}: {
  open: boolean;
  onBack?: () => void;
  onClose?: () => void;
  eyebrow?: string;
  eyebrowClass?: string;
  title?: string;
  z?: string;
  padded?: boolean;
  direction?: 'right' | 'up';
  scroll?: boolean;
  children: React.ReactNode;
}) {
  const hidden = direction === 'up' ? 'translate-y-full' : 'translate-x-full';
  const shown = direction === 'up' ? 'translate-y-0' : 'translate-x-0';
  return <div className={`absolute inset-0 ${z} flex flex-col bg-linen transition-transform duration-500 ease-[cubic-bezier(0.22,0.8,0.36,1)] ${open ? shown : hidden} ${open ? '' : 'pointer-events-none'}`} aria-hidden={!open} data-id="element-880">
      {/* header */}
      <header className="shrink-0 px-5 pt-12" data-id="element-881">
        <div className="flex items-center justify-between" data-id="element-882">
          {onBack ? <button onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-full bg-cream text-ink shadow-card transition-transform active:scale-90" aria-label="Back" data-id="element-883">
              <ArrowLeftIcon size={18} data-id="element-884" />
            </button> : <span className="h-10 w-10" data-id="element-885" />}
          {onClose && <button onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-cream text-ink-soft shadow-card transition-transform hover:text-ink active:scale-90" aria-label="Close" data-id="element-886">
              <XIcon size={18} data-id="element-887" />
            </button>}
        </div>
        {(eyebrow || title) && <div className="mt-3" data-id="element-888">
            {eyebrow && <p className={`text-[10.5px] font-bold uppercase tracking-[0.16em] ${eyebrowClass}`} data-id="element-889">
                {eyebrow}
              </p>}
            {title && <h1 className="font-display text-[24px] font-semibold leading-tight text-ink" data-id="element-890">
                {title}
              </h1>}
          </div>}
      </header>

      {/* body — when scroll is false, the child owns scrolling */}
      <div className={scroll ? `flex-1 overflow-y-auto pb-32 ${padded ? 'px-5 pt-3' : ''}` : 'flex min-h-0 flex-1 flex-col'} data-id="element-891">
        {children}
      </div>
    </div>;
}